<template>
  <div class="app">
    <div class="view-container">
      <transition
        @enter="enter"
        @leave="leave"
        mode="out-in"
        appear
      >
        <router-view
          class="app__page-container"
          :key="$route.fullPath"
        />
      </transition>
    </div>

    <AppCursor />
    <Navigator v-if="$route.params.city !== undefined" />
    <Info v-if="$route.name !== 'workshop' && $route.name !== 'speaker'" />
    <transition name="fade-message">
      <NewContentButton
      :key="$route.params.city !== undefined && $store.state.message[$route.params.city].toDisplay"
      v-if="$route.params.city !== undefined && $store.state.message[$route.params.city].toDisplay"
      />
    </transition>

    <Cookiebar
      class="app__cookie"
      :animation="{ enter: enterCookie, leave: leaveCookie, css: false }"
    >
      <CookieContent
        slot="content"
        text="This website use "
        url="https://www.awwwards.com/cookies-policy/"
        label="cookies."
      />
      <CookieButton
        slot="button"
        label="Accept"
      />
    </Cookiebar>
  </div>
</template>

<script>
import anime from 'animejs';
import debounce from 'lodash/debounce';

import raf from '@/scripts/raf';
import medusa from '@/scripts/medusa';
import hades from '@/scripts/hades';

import AppCursor from '@/components/AppCursor.vue';
import Navigator from '@/components/Navigator.vue';
import Cookiebar from '@/components/Cookie/index.vue';
import CookieContent from '@/components/Cookie/CookieContent/index.vue';
import CookieButton from '@/components/Cookie/CookieButton/index.vue';
import Info from '@/components/Info.vue';
import NewContentButton from '@/components/NewContentButton.vue';
import { animations } from './scripts/fx';

require('intersection-observer');


export default {
  name: 'app',
  components: {
    AppCursor,
    Navigator,
    Info,
    Cookiebar,
    CookieContent,
    CookieButton,
    NewContentButton,
  },
  created() {
    medusa.init();
  },
  mounted() {
    this.initUtils();
    this.resize();
    window.addEventListener('resize', debounce(() => this.resize(), 200));
  },
  methods: {
    initUtils() {
      raf.start();
      hades.init();
    },
    resize() {
      this.$eventHub.$emit('windowResize');
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    },
    enter(el, done) {
      if (hades.ref !== null) hades.ref.scrollTo({ y: 0 }, 0);

      this.$eventHub.$emit('morphCursor', 'rect');

      if (this.$route.params.city !== document.body.getAttribute('data-city')) {
        document.body.setAttribute(
          'data-city',
          this.$route.params.city !== undefined ? this.$route.params.city : 'welcome',
        );
      }

      if (el.classList.contains('welcome')) {
        const blocks = [...el.querySelectorAll('.index-block')];
        anime.set(blocks, {
          clipPath: (element, i) => { // eslint-disable-line
            return i % 2 === 0 ? 'inset(100% 0 0 0)' : 'inset(0 0 100% 0)';
          },
        });
        anime({
          targets: blocks,
          clipPath: (el, i) => { // eslint-disable-line
            return i % 2 === 0 ? ['inset(100% 0 0 0)', 'inset(0% 0 0 0)'] : ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'];
          },
          easing: 'spring(4, 100, 30, 7)',
          complete: done,
        });
      } else {
        anime({
          targets: el,
          translateY: [200, 0],
          opacity: [0, 1],
          duration: 1500,
          complete: () => {
            this.$store.commit('SET_IN_TRANSITION', false);
            done();
          },
        });
      }
    },
    leave(el, done) {
      this.$store.commit('SET_IN_TRANSITION', true);
      if (this.$store.state.isMenuOpen) {
        this.$store.commit('SET_MENU_STATE', false);
        animations.get('menu-close')(document.querySelector('.navigator__container'));
      }

      if (el.classList.contains('welcome')) {
        let isDone = false;

        if (window.innerWidth < 768) {
          anime({
            targets: el,
            opacity: [1, 0],
            easing: 'spring(4, 100, 30, 7)',
            update: (anim) => {
              if (Math.round(anim.progress) > 45 && !isDone) {
                isDone = true;
                done();
              }
            },
          });
        } else {
          const blocks = [...el.querySelectorAll('.index-block')];
          anime({
            targets: blocks,
            clipPath: (el, i) => { // eslint-disable-line
              return i % 2 === 0 ? ['inset(0 0 0 0)', 'inset(0 0 100% 0)'] : ['inset(0 0 0 0)', 'inset(100% 0 0)'];
            },
            easing: 'spring(4, 100, 30, 6)',
            complete: done,
            update: (anim) => {
              if (Math.round(anim.progress) > 45 && !isDone) {
                isDone = true;
                done();
              }
            },
          });
        }
      } else {
        anime({
          targets: el,
          translateY: {
            value: -400,
            delay: 300,
          },
          opacity: {
            value: 0,
          },
          duration: 600,
          delay: 100,
          easing: 'easeInCirc',
          complete: () => {
            if (this.$route.params.city !== document.body.getAttribute('data-city')) {
              document.body.setAttribute(
                'data-city',
                this.$route.params.city !== undefined ? this.$route.params.city : 'welcome',
              );
            }
            done();
          },
        });
      }
    },
    enterCookie(el, done) {
      anime({
        targets: el,
        translateY: [100, 0],
        opacity: [0, 1],
        complete: done,
      });
    },
    leaveCookie(el, done) {
      anime({
        targets: el,
        translateY: [0, 100],
        opacity: [1, 0],
        complete: () => {
          el.remove();
          done();
        },
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/scss/static';
@import '@/scss/base';

@each $city in map-keys($palettes) {
  body[data-city="#{$city}"] {
    @each $type in map-keys(map-get($palettes, $city)) {
      --color-#{$type}: #{map-deep-get($palettes, $city, $type)};
    }
    background-color: var(--color-bg);
  }
}

body {
  background-color: #96BFE6;
  transition: background-color 1s $outBack;

  cursor: none;
}

h1 {
  font-size: 50px;
}

.title__char {
  backface-visibility: hidden;
}

.c-title {
  &--light {
    color: var(--color-headline-light);
  }

  &--dark {
    color: var(--color-headline-dark);
  }
}

body {
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
}

.app {
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  .view-container {
    position: relative;
    min-height: 100%;
  }

  &__page-container {
    padding: $unit-vh * 8 20px 20px;

    @include mq(ipadP) {
      padding: $unit-vh * 8 $unit-vw * 4 $unit-vw * 4 $unit-vw * 8;
    }

    @include mq(ds) {
      padding: $unit-vh * 8 $unit-vw * 4 $unit-vh * 4 $unit-vw * 8;
    }

    &[type="child"] {
      @include mq(ipadP) {
        padding: $unit-vh * 8 $unit-vw * 4 $unit-vw * 4 $unit-vw * 4;
      }

      @include mq(ipadL) {
        padding: $unit-vh * 8 $unit-vw * 4 $unit-vw * 4 $unit-vw * 8;
      }

      @include mq(ds) {
        padding: $unit-vh * 8 $unit-vw * 4 $unit-vh * 4 $unit-vw * 8;
      }
    }
  }

  main > section {
    position: relative;
    margin-top: $unit-vh * 7;

    &:first-of-type {
      margin-top: 0;
    }

    @include mq(ipadP) {
      margin-top: $unit-vh * 6;
    }

    @include mq(ds) {
      margin-top: $unit-vh * 12;
    }
  }
}

.app .welcome.app__page-container .c-picture.picture  {
  background-color: transparent;
}

.app .welcome.app__page-container {
  margin: 0;
  padding: 0;

  display: flex;
}

[data-city="welcome"] .app .cursor > svg > rect {
  fill: #F06F8F;
  mix-blend-mode: color-burn;
}

.fade-message-enter-active,
.fade-message-leave-active {
  transition: opacity .5s $outCirc,
    transform .5s $outCirc;
}

.fade-message-enter,
.fade-message-leave-to {
  position: absolute;
  opacity: 0;
  transform: translateY(20px);
}
</style>
